document.addEventListener("DOMContentLoaded", () => {
  const ANIMATE_CLASS = "animate";
  const ELEMENT_SELECTOR = ".lava";
  const CONTAINER_SELECTOR = ".lavaCont";
  const STAGGER_DELAY = 300;
  const DEFAULT_CONTAINER_OFFSET = 15; // % from bottom of viewport
  const DEFAULT_ELEMENT_OFFSET = 15; // % from bottom of viewport

  // If user prefers reduced motion, show all elements immediately
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    document
      .querySelectorAll(ELEMENT_SELECTOR)
      .forEach((el) => el.classList.add(ANIMATE_CLASS));
    return;
  }

  // #region Scroll sweep

  // Scroll-scrubbed background sweep (steps block). The capsule's translateX
  // is driven by how far its section has travelled up through the viewport:
  // hidden off the left when the section enters, full coverage once it is
  // comfortably in view. Progress is clamped, so scrolling back up reverses it.
  // NOTE: this lives BEFORE the `.lava` early-return below — pages with the
  // steps block but no .lava elements (e.g. the homepage) must still scrub.
  const SWEEP_START = -104; // translateX % when hidden
  const SWEEP_END = -17; // translateX % at full coverage (cap past step 3)
  const SWEEP_ENTER = 0.95; // section top as a fraction of viewport height → p=0
  const SWEEP_FULL = 0.2; // section top as a fraction of viewport height → p=1
  const SWEEP_STEP = 0.2; // quantise progress into 20% chunks → the capsule
  // snaps forward one piece at a time as you scroll past each threshold.
  const SWEEP_SMOOTH = 0.09; // per-frame easing toward the (stepped) target →
  // each chunk eases in with a soft delay instead of jumping instantly.

  // Mobile steps row auto-scroll range — measured on the ROW itself (not the
  // whole section) so step 1 stays readable while the steps settle into view,
  // and the row only advances to steps 2/3 as you keep scrolling. Higher ENTER
  // = step 1 held longer; lower FULL = step 3 revealed later.
  const STEP_SCROLL_ENTER = 0.5; // row top / vh where auto-scroll begins (step 1 held until the row reaches mid-screen — readable)
  const STEP_SCROLL_FULL = 0.25; // row top / vh where step 3 is fully revealed (still comfortably in view, not scrolling off)

  const sweepElements = [...document.querySelectorAll("[data-steps-sweep]")].map(
    (el) => ({
      el,
      section: el.closest("section") || el.parentElement,
      current: null,
      target: SWEEP_START,
    })
  );

  // The mobile steps row scrolls horizontally in step with the page: as its
  // section travels up the viewport, the row scrolls from step 1 → step 3 so
  // the last step reveals itself without a manual swipe. On desktop the row has
  // no horizontal overflow (3 equal columns), so maxScroll is 0 and this no-ops.
  const stepsScrollers = [...document.querySelectorAll("[data-steps-scroll]")].map(
    (el) => ({ el, section: el.closest("section") || el.parentElement })
  );

  // Raw 0→1 progress of a section travelling up through the viewport.
  function sectionProgress(section) {
    const vh = window.innerHeight;
    const startY = vh * SWEEP_ENTER;
    const fullY = vh * SWEEP_FULL;
    const p = (startY - section.getBoundingClientRect().top) / (startY - fullY);
    return Math.min(1, Math.max(0, p));
  }

  // Recompute each capsule's target position from its section's scroll progress.
  function computeSweepTargets() {
    sweepElements.forEach((s) => {
      // Quantise into 20% chunks so a new piece only appears once you scroll
      // past each threshold (stepped reveal), then eased in by the lerp below.
      const p = Math.round(sectionProgress(s.section) / SWEEP_STEP) * SWEEP_STEP;
      s.target = SWEEP_START + p * (SWEEP_END - SWEEP_START);
    });
  }

  // Drive the mobile steps row's horizontal scroll from ITS OWN position in the
  // viewport, so step 1 stays put until the row is comfortably in view.
  function updateStepsScroll() {
    const vh = window.innerHeight;
    const startY = vh * STEP_SCROLL_ENTER;
    const fullY = vh * STEP_SCROLL_FULL;
    stepsScrollers.forEach(({ el }) => {
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return; // desktop / no overflow → nothing to auto-scroll
      const top = el.getBoundingClientRect().top;
      const p = Math.min(1, Math.max(0, (startY - top) / (startY - fullY)));
      el.scrollLeft = max * p;
    });
  }

  // Ease each capsule toward its target; keep looping until everything settles.
  let sweepRaf = null;
  function sweepFrame() {
    let moving = false;
    sweepElements.forEach((s) => {
      if (s.current === null) s.current = s.target; // snap on first paint, no intro lag
      const diff = s.target - s.current;
      if (Math.abs(diff) > 0.05) {
        s.current += diff * SWEEP_SMOOTH;
        moving = true;
      } else {
        s.current = s.target;
      }
      s.el.style.setProperty("--sweep", `${s.current.toFixed(2)}%`);
    });
    sweepRaf = moving ? requestAnimationFrame(sweepFrame) : null;
  }

  let sweepScrollQueued = false;
  function onSweepScroll() {
    if (sweepScrollQueued) return;
    sweepScrollQueued = true;
    requestAnimationFrame(() => {
      sweepScrollQueued = false;
      computeSweepTargets();
      updateStepsScroll();
      if (sweepRaf === null) sweepRaf = requestAnimationFrame(sweepFrame);
    });
  }

  if (sweepElements.length || stepsScrollers.length) {
    computeSweepTargets();
    updateStepsScroll();
    sweepFrame(); // initial paint (snaps current→target on the first frame)
    window.addEventListener("scroll", onSweepScroll, { passive: true });
    window.addEventListener("resize", onSweepScroll, { passive: true });
  }

  // #endregion Scroll sweep

  function toRootMargin(offset) {
    return `-${Math.max(0, offset - 5)}% 0px -${Math.max(0, offset)}% 0px`;
  }

  // #region Count-up

  // Stats-cijfers tellen op van 0 zodra ze in beeld scrollen. De Twig-markup
  // rendert de EINDwaarde server-side (SEO / no-JS); JS parseert die, zet de
  // breedte vast zodat het gele bolletje niet verschuift tijdens het tellen,
  // en animeert. Waarden met prefix/suffix rond ÉÉN getal animeren enkel het
  // numerieke deel ("500+" → telt naar 500, "+" blijft staan). Alles wat geen
  // los getal is ("24/7", "za & zo") blijft onaangeroerd staan.
  // NOTE: dit staat BEWUST vóór de `.lava` early-return hieronder — pagina's
  // met het stats-blok maar zonder .lava-elementen moeten óók tellen.
  const COUNT_DURATION = 1600; // ms
  const COUNT_OFFSET = 15; // % vanaf onderkant viewport, zelfde gevoel als .lava

  // "10.000+" → { prefix: "", value: 10000, thousands: ".", suffix: "+" }
  // "4,8"     → { value: 4.8, decimals: 1, decimalSep: "," }
  // "24/7"    → null (geen los getal → niet animeren)
  function parseCountTarget(text) {
    const m = text.trim().match(/^(\D*?)([\d.,]+)(\D*)$/);
    if (!m) return null;
    const [, prefix, raw, suffix] = m;

    let thousands = "";
    let decimals = 0;
    let decimalSep = ",";
    let value;

    const seps = raw.match(/[.,]/g) || [];
    const lastSep = Math.max(raw.lastIndexOf("."), raw.lastIndexOf(","));
    if (!seps.length) {
      value = parseInt(raw, 10);
    } else if (new Set(seps).size === 2) {
      // beide tekens aanwezig ("1.234,5") → laatste is decimaal, andere duizendtal
      decimalSep = raw[lastSep];
      thousands = decimalSep === "," ? "." : ",";
      decimals = raw.length - lastSep - 1;
      value = parseFloat(raw.replaceAll(thousands, "").replace(decimalSep, "."));
    } else if (seps.length > 1 || raw.length - lastSep - 1 === 3) {
      // herhaald ("1.200.000") of exact 3 cijfers erna ("10.000") → duizendtallen
      thousands = seps[0];
      value = parseInt(raw.replaceAll(thousands, ""), 10);
    } else {
      // één separator met 1-2 cijfers erna ("4,8") → decimaal
      decimalSep = seps[0];
      decimals = raw.length - lastSep - 1;
      value = parseFloat(raw.replace(decimalSep, "."));
    }

    if (isNaN(value)) return null;
    return { prefix, suffix, value, thousands, decimals, decimalSep };
  }

  // Formatteer een tussenwaarde in hetzelfde formaat als de eindwaarde.
  function formatCount(n, { thousands, decimals, decimalSep }) {
    const [int, dec] = n.toFixed(decimals).split(".");
    const grouped = thousands
      ? int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands)
      : int;
    return dec ? grouped + decimalSep + dec : grouped;
  }

  function animateCount(el, parsed) {
    const start = performance.now();
    function frame(now) {
      const p = Math.min(1, (now - start) / COUNT_DURATION);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      el.textContent =
        parsed.prefix + formatCount(parsed.value * eased, parsed) + parsed.suffix;
      if (p < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  const countElements = [...document.querySelectorAll("[data-count-up]")]
    .map((el) => ({ el, parsed: parseCountTarget(el.textContent) }))
    .filter((c) => c.parsed);

  if (countElements.length) {
    const countObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          const c = countElements.find(({ el }) => el === entry.target);
          animateCount(c.el, c.parsed);
        });
      },
      { rootMargin: toRootMargin(COUNT_OFFSET) }
    );

    countElements.forEach(({ el, parsed }) => {
      // Breedte vastzetten op de eindwaarde en rechts uitlijnen: het getal
      // groeit naar links en het bolletje ernaast blijft perfect op zijn plek.
      el.style.minWidth = `${el.getBoundingClientRect().width}px`;
      el.style.textAlign = "right";
      el.textContent = parsed.prefix + formatCount(0, parsed) + parsed.suffix;
      countObserver.observe(el);
    });
  }

  // #endregion Count-up

  // Separate container-managed elements from standalone ones
  const containerElements = new Set(
    document.querySelectorAll(`${CONTAINER_SELECTOR} ${ELEMENT_SELECTOR}`)
  );
  const containers = document.querySelectorAll(CONTAINER_SELECTOR);
  const standaloneElements = [
    ...document.querySelectorAll(ELEMENT_SELECTOR),
  ].filter((el) => !containerElements.has(el));

  if (!containers.length && !standaloneElements.length) return;


  // #region Containers

  // Track active timeouts per container to prevent stacking on rapid scroll
  const containerTimeouts = new Map();

  function clearPendingTimeouts(container) {
    const timeouts = containerTimeouts.get(container);
    if (timeouts) {
      timeouts.forEach(clearTimeout);
      containerTimeouts.delete(container);
    }
  }

  function staggerAnimateIn(container) {
    clearPendingTimeouts(container);
    const children = container.querySelectorAll(ELEMENT_SELECTOR);
    const timeouts = [];
    children.forEach((el, i) => {
      const id = setTimeout(
        () => el.classList.add(ANIMATE_CLASS),
        i * STAGGER_DELAY
      );
      timeouts.push(id);
    });
    containerTimeouts.set(container, timeouts);
  }

  function resetContainer(container) {
    clearPendingTimeouts(container);
    container
      .querySelectorAll(ELEMENT_SELECTOR)
      .forEach((el) => el.classList.remove(ANIMATE_CLASS));
  }

  // Container observer callback (shared across all offset-specific observers)
  // Use data-lava-reset to reset animation on scroll out
  function handleContainerIntersection(entries, observer) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        staggerAnimateIn(entry.target);
        if (entry.target.dataset.lavaReset === undefined) {
          observer.unobserve(entry.target);
        }
      } else {
        resetContainer(entry.target);
      }
    });
  }

  // Group containers by offset, creating one observer per unique value
  // Use data-lava-offset to override the default per container
  const containersByOffset = new Map();

  containers.forEach((el) => {
    const parsed = parseFloat(el.dataset.lavaOffset);
    const offset = isNaN(parsed) ? DEFAULT_CONTAINER_OFFSET : parsed;
    if (!containersByOffset.has(offset)) {
      containersByOffset.set(offset, []);
    }
    containersByOffset.get(offset).push(el);
  });

  containersByOffset.forEach((elements, offset) => {
    const observer = new IntersectionObserver(handleContainerIntersection, {
      rootMargin: toRootMargin(offset),
    });
    elements.forEach((el) => observer.observe(el));
  });

  // #endregion Containers


  // #region Elements

  // Observe standalone elements — trigger when element enters viewport offset
  // Use data-lava-reset to reset animation on scroll out
  function handleElementIntersection(entries, observer) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add(ANIMATE_CLASS);
        if (entry.target.dataset.lavaReset === undefined) {
          observer.unobserve(entry.target);
        }
      } else {
        entry.target.classList.remove(ANIMATE_CLASS);
      }
    });
  }

  // Group standalone elements by offset, creating one observer per unique value
  // Use data-lava-offset to override the default per element
  const elementsByOffset = new Map();

  standaloneElements.forEach((el) => {
    const parsed = parseFloat(el.dataset.lavaOffset);
    const offset = isNaN(parsed) ? DEFAULT_ELEMENT_OFFSET : parsed;
    if (!elementsByOffset.has(offset)) {
      elementsByOffset.set(offset, []);
    }
    elementsByOffset.get(offset).push(el);
  });

  elementsByOffset.forEach((elements, offset) => {
    const observer = new IntersectionObserver(handleElementIntersection, {
      rootMargin: toRootMargin(offset),
    });
    elements.forEach((el) => observer.observe(el));
  });

  // #endregion Elements

  // #region Parallax

  const DEFAULT_PARALLAX_SPEED = -0.16;
  const rootStyles = getComputedStyle(document.documentElement);

  // Parse a data-parallax value into a pixel breakpoint and mode.
  // Syntax: "" (always), "lg" (min-width), "max-lg" (max-width),
  //         "1024" (min-width), "max-1024" (max-width).
  function parseParallaxValue(value) {
    if (!value) return { px: 0, mode: null };

    const isMax = value.startsWith("max-");
    const name = isMax ? value.slice(4) : value;

    // Try CSS custom property first (handles "2xl", "3xl", etc.)
    const cssVal = parseInt(rootStyles.getPropertyValue(`--breakpoint-${name}`), 10);
    const px = !isNaN(cssVal) ? cssVal : (parseInt(name, 10) || 0);

    if (px === 0) return { px: 0, mode: null };
    return { px, mode: isMax ? "max" : "min" };
  }

  // Group elements by breakpoint key
  const parallaxGroups = new Map(); // key → { mql, elements[] }
  const trackedParallaxElements = new Set();

  function registerParallaxElements(elements) {
    elements.forEach((el) => {
      if (trackedParallaxElements.has(el)) return;
      trackedParallaxElements.add(el);

      const { px, mode } = parseParallaxValue(el.dataset.parallax);
      const key = mode ? `${mode}-${px}` : "always";

      if (!parallaxGroups.has(key)) {
        let mql = null;
        if (mode === "min") mql = window.matchMedia(`(min-width: ${px}px)`);
        else if (mode === "max") mql = window.matchMedia(`(max-width: ${px - 1}px)`);

        parallaxGroups.set(key, { mql, elements: [] });

        // When a breakpoint stops matching, clear transforms
        if (mql) {
          mql.addEventListener("change", (e) => {
            if (!e.matches) {
              parallaxGroups.get(key).elements.forEach((el) => (el.style.transform = ""));
            }
          });
        }
      }
      parallaxGroups.get(key).elements.push(el);
    });
  }

  function updateParallax() {
    parallaxGroups.forEach(({ mql, elements }) => {
      if (mql && !mql.matches) return; // skip inactive breakpoint groups

      elements.forEach((el) => {
        const speed = parseFloat(el.dataset.parallaxSpeed) || DEFAULT_PARALLAX_SPEED;
        const rect = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const offset = (window.innerHeight / 2 - center) * speed;
        el.style.transform = `translateY(${offset}px)`;
      });
    });
  }

  let parallaxTicking = false;
  function onParallaxScroll() {
    if (!parallaxTicking) {
      requestAnimationFrame(() => {
        updateParallax();
        parallaxTicking = false;
      });
      parallaxTicking = true;
    }
  }

  const initialParallaxElements = document.querySelectorAll("[data-parallax]");
  registerParallaxElements(initialParallaxElements);

  if (initialParallaxElements.length) {
    window.addEventListener("scroll", onParallaxScroll, { passive: true });
    window._parallaxScrollActive = true;
  }

  // #endregion Parallax
});